import { Button, Card, Divider, Stack, Typography } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { useSWRAllPages, useUsers } from "../../hooks";

import { AddUserRoleForm } from "./AddUserRoleForm";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { BACKEND_ROUTES } from "../../backendRoutes";
import { FetchErrorAlert } from "../../components/FetchErrorAlert";
import { NavigationBreadcrumbs } from "../../components";
import { UserRoleTable } from "./UserRoleTable";
import { UserRoleTableSkeleton } from "./UserRoleTableSkeleton";
import { getUserRoleScope } from "./utils";
import useSWR from "swr";
import { useState } from "react";

const USER_ROLE_PAGE_SIZE = 50;
const ASSIGNED_USERS_PAGE_SIZE = 200;
// Backend maximum for the role list
const ROLE_PAGE_SIZE = 200;

export function UserRoleManagement() {
    const scope = getUserRoleScope(useParams());
    const [page, setPage] = useState(0);

    const { data: entity, error: entityError } = useSWR(scope.entityUrl);
    const { data: roles, error: rolesError } = useSWRAllPages(
        `${BACKEND_ROUTES.ROLE}?permissions=true`,
        ROLE_PAGE_SIZE
    );
    const { data: users, error: usersError } = useUsers();

    const userRolesUrl = `${BACKEND_ROUTES.USER_ROLE}?${scope.type}Uuid=${scope.uuid}`;
    const {
        data: userRoles,
        error: userRolesError,
        mutate: mutateUserRoles,
    } = useSWR(
        `${userRolesUrl}${scope.levelFilter}&user=true&role=true&limit=${USER_ROLE_PAGE_SIZE}&offset=${page * USER_ROLE_PAGE_SIZE}`
    );
    // Every page, so users assigned on other pages are excluded from the form.
    // Level filtered client-side: useSWRAllPages turns a bare key into `key=`
    const { data: allUserRoles, mutate: mutateAllUserRoles } = useSWRAllPages(
        userRolesUrl,
        ASSIGNED_USERS_PAGE_SIZE
    );

    const assignedUserUuids = new Set(
        allUserRoles?.filter(scope.isOwnLevel).map((ur) => ur.userUuid)
    );
    const unassignedUsers = (users ?? []).filter(
        (u) => !assignedUserUuids.has(u.uuid)
    );

    const refresh = () =>
        Promise.all([mutateUserRoles(), mutateAllUserRoles()]);

    const handleDeleted = () => {
        if (userRoles.rows.length === 1 && page > 0) setPage(page - 1);
        refresh();
    };

    const fetchError =
        entityError ?? rolesError ?? usersError ?? userRolesError;
    const count = userRoles?.count ?? 0;

    return (
        <Stack spacing={2}>
            <Stack spacing={0.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Button
                        component={Link}
                        to={scope.backPath}
                        startIcon={<ArrowBackIcon />}
                        variant="outlined"
                        size="small"
                        color="inherit"
                        sx={{ color: "text.secondary", flexShrink: 0 }}
                    >
                        Back
                    </Button>
                    <Divider orientation="vertical" flexItem />
                    {entity && (
                        <NavigationBreadcrumbs
                            {...scope.getBreadcrumbs(entity)}
                        />
                    )}
                </Stack>
                <Typography variant="h4">
                    User roles — {entity?.displayName ?? entity?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Manage users and their permissions
                </Typography>
            </Stack>

            {fetchError ? (
                <FetchErrorAlert error={fetchError} />
            ) : (
                <>
                    <AddUserRoleForm
                        scope={scope}
                        users={unassignedUsers}
                        roles={roles ?? []}
                        onAdded={refresh}
                    />
                    <Card sx={{ p: 3 }}>
                        {userRoles ? (
                            <UserRoleTable
                                userRoles={userRoles.rows}
                                count={count}
                                page={page}
                                pageCount={Math.ceil(
                                    count / USER_ROLE_PAGE_SIZE
                                )}
                                onPageChange={setPage}
                                roles={roles ?? []}
                                onUpdated={refresh}
                                onDeleted={handleDeleted}
                            />
                        ) : (
                            <UserRoleTableSkeleton />
                        )}
                    </Card>
                </>
            )}
        </Stack>
    );
}
